import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { chartAlpha, CHART_INK, seriesColor } from '../constants/chartPalette';
import { toneFor } from '../constants/statTones';
import {
  ChartAxisLabels,
  ChartDataPoint,
  ChartGrid,
  ChartLegend,
  ChartValueAxis,
  formatRatio,
  formatValue,
  niceScale,
  pointsToPlot,
  polylinePath,
  seriesChartToPlot,
  stackedBandPath,
  WidgetPeriodChip,
} from '../shared';

/**
 * Floor for the plot area. The plot itself flexes to whatever height the slot
 * gives it — a chart in a tall widget should draw a tall chart, not the same
 * 96px strip with air underneath — and this is the height below which the
 * bars stop being comparable at a glance.
 */
const PLOT_MIN_PX = 96;

/** Where the last point of a sparkline's only series sits, as text. */
const lastOf = (values) => values[values.length - 1];

/**
 * A trend over time.
 *
 * Three variants, because they are one drawing read at three densities:
 *
 * - `line` — the plot with a legend, gridlines and axis labels.
 * - `area` — the same, **stacked**, with every series filled. Reach for it
 *   when the quantity underneath the trend is the point, and when the parts
 *   add up to something: the top edge of the last band is the total.
 *
 *   It used to draw only the first series, on the reasoning that one filled
 *   area hides the one behind it. That is true of overlaid areas and it is
 *   exactly what stacking answers — the contract's own example sends four
 *   series with `display.stack_mode: NORMAL`, so drawing one of them silently
 *   dropped three quarters of the chart.
 * - `sparkline` — no axis, no legend, no grid, sized to sit beside a number.
 *   Everything the other two draw would compete with that number. It prints
 *   its `summary_value` and, under it, `delta_pct` — the field that makes a
 *   sparkline a reading rather than an ornament. The last point is marked,
 *   because that dot is what the big number refers to.
 *
 * ## Reading the contract (CT-5641)
 *
 * The four plotted types publish `series_chart` — an x axis, the y axes and a
 * list of series whose points are `{x, y}` pairs. The drawing wants parallel
 * arrays, so `seriesChartToPlot` converts; see its note for why aligning by
 * `x` rather than by position matters, and where a series' colour comes from.
 *
 * A `SPARKLINE` publishes a bare list of points instead, lifted into the same
 * shape by `pointsToPlot`, plus a `summary_value` it shows beside the line —
 * which is the whole reason a sparkline is not just a small line chart.
 *
 * Values are plotted against the peak of everything drawn — the tallest
 * stack, for an area. Raw counts are fine; the caller no longer has to
 * pre-scale to 0-100, which was a rule nobody could follow once the numbers
 * came from a backend.
 *
 * `fillGaps` is the contract's `display.fill_gaps`. `'GAP'` breaks the line at
 * a missing point, which is right for a trend — a region with no sales in
 * September did not sell zero. `'ZERO'` reads the hole as a real zero, which
 * is what the stacked types ask for and what stacking requires: a band with no
 * floor under it has nowhere to sit.
 *
 * Hover targets are invisible until hovered. The chart stays clean and still
 * answers "what is that peak in April".
 *
 * @param {object} props
 * @param {object} props.data `series_chart`, or a `SPARKLINE`'s points.
 * @param {'line'|'area'|'sparkline'} [props.variant='line']
 * @param {'GAP'|'ZERO'} [props.fillGaps='GAP'] What a missing point means.
 * @param {'top'|'bottom'} [props.legendPosition='top'] `display.legend.position`.
 * @param {boolean} [props.fill=false] A sparkline's `display.variant: AREA`.
 * @param {'UP'|'DOWN'} [props.positiveDirection='UP'] Which way is good, for
 *   the sparkline's delta colour. Up is bad for cost, waste or time to fix.
 * @param {string} [props.accent] Overrides the first series' colour.
 * @param {string} [props.className]
 */
function LineChartWidget({
  data,
  variant = 'line',
  fillGaps = 'GAP',
  legendPosition = 'top',
  fill = false,
  positiveDirection = 'UP',
  accent,
  className = '',
}) {
  const isArea = variant === 'area';
  const isSparkline = variant === 'sparkline';

  // Three shapes reach this drawing: the contract's `series_chart`, a
  // sparkline's bare points, and the parallel arrays a pre-v2 caller passes.
  const plot = useMemo(() => {
    if (!data) return null;
    if (Array.isArray(data.series) && data.series.some((entry) => entry.points)) {
      return seriesChartToPlot(data, { accent });
    }
    if (Array.isArray(data.points)) return pointsToPlot(data, { accent });
    if (Array.isArray(data.series)) {
      return {
        categories: data.categories ?? [],
        series: data.series.map((entry, index) => ({
          key: entry.label ?? String(index),
          label: entry.label ?? '',
          values: entry.values ?? [],
          color: seriesColor(index, accent),
          isOthers: false,
        })),
        format: null,
        axis: null,
      };
    }
    return null;
  }, [accent, data]);

  const categories = plot?.categories ?? [];
  const axis = data?.y_axes?.[0] ?? null;
  const showMarkers = variant === 'line';

  /*
    A sparkline is single-series by definition — one with a legend is a line
    chart. An area is not: it stacks, so every series is drawn.

    `fillGaps: 'ZERO'` is applied here rather than at the draw, because both
    the stacking arithmetic and the scaling have to see the same values.
  */
  const drawn = useMemo(() => {
    const series = plot?.series ?? [];
    const kept = isSparkline ? series.slice(0, 1) : series;
    if (fillGaps !== 'ZERO') return kept;
    return kept.map((entry) => ({
      ...entry,
      values: entry.values.map((value) => (typeof value === 'number' ? value : 0)),
    }));
  }, [fillGaps, isSparkline, plot]);

  /*
    The ceiling the plot is drawn against: a round number above the largest
    thing on it — the tallest stack for an area, the tallest point otherwise —
    so the axis reads in whole units and the top mark does not touch the
    frame. See `niceScale`.
  */
  const scale = useMemo(() => {
    const peak = isArea
      ? Math.max(0, ...(drawn[0]?.values ?? []).map((_, index) =>
        drawn.reduce((sum, entry) => sum + (entry.values[index] ?? 0), 0)))
      : Math.max(
        0,
        ...drawn.flatMap((entry) => entry.values.filter((value) => typeof value === 'number')),
      );
    return niceScale(peak);
  }, [drawn, isArea]);

  /*
    A sparkline scales to its own range, not from zero.

    The axis charts start at zero because they have an axis: a bar is a length
    from nothing, and a gridline labelled 0 is what makes that readable. A
    sparkline has no axis at all, so a zero floor buys nothing and costs
    everything — weekly orders between 88 and 121, drawn against a ceiling of
    150, is a flat line, which is the opposite of what the reader is being
    shown. The contract sends `min` and `max` for exactly this.

    `floor` is subtracted before scaling, so the shape fills the strip.
  */
  const sparkFloor = useMemo(() => {
    if (!isSparkline) return 0;
    const values = drawn.flatMap((entry) => entry.values.filter((v) => typeof v === 'number'));
    if (!values.length) return 0;
    return data?.min ?? Math.min(...values);
  }, [data, drawn, isSparkline]);

  const ceiling = isSparkline
    ? (data?.max ?? Math.max(
      0,
      ...drawn.flatMap((entry) => entry.values.filter((v) => typeof v === 'number')),
    )) - sparkFloor
    : scale.max;

  /*
    The contract sends real numbers; the geometry plots on 0-100.

    Scaling against **one ceiling for every drawn series** rather than each
    series' own peak is what keeps two lines comparable: normalising each one
    separately would draw a region selling 45,000 and one selling 12,000 at the
    same height. Nulls stay null — see `polylinePath`.
  */
  const plotted = useMemo(() => {
    /*
      Stacked: each series sits on the running total of the ones before it, so
      the scale is the tallest *stack*, not the tallest series. `lower` and
      `upper` are that band's two boundaries; `values` stays the band's top
      edge, which is what the line and the hover targets follow.
    */
    if (isArea) {
      const length = drawn[0]?.values.length ?? 0;
      const running = new Array(length).fill(0);
      const bands = drawn.map((entry) => {
        const lower = [...running];
        const upper = entry.values.map((value, index) => {
          running[index] += typeof value === 'number' ? value : 0;
          return running[index];
        });
        return { ...entry, lower, upper, raw: entry.values };
      });
      if (!(ceiling > 0)) return bands;
      const to100 = (value) => (value / ceiling) * 100;
      return bands.map((band) => ({
        ...band,
        lower: band.lower.map(to100),
        upper: band.upper.map(to100),
        values: band.upper.map(to100),
      }));
    }

    if (!(ceiling > 0)) return drawn;
    return drawn.map((entry) => ({
      ...entry,
      values: entry.values.map(
        (value) => (typeof value === 'number' ? ((value - sparkFloor) / ceiling) * 100 : null),
      ),
      raw: entry.values,
    }));
  }, [ceiling, drawn, isArea, sparkFloor]);

  if (!categories.length || !plotted.length) return null;

  const legendItems = plotted.map((entry) => ({ label: entry.label, color: entry.color }));

  if (isSparkline) {
    const values = plotted[0].values;
    // A sparkline's point is its `summary_value`, which the contract resolves
    // (LAST, SUM or AVG, as `display.summary` asked). Falling back to the last
    // raw point keeps a pre-v2 payload readable.
    const headline = data?.summary_value ?? lastOf(plotted[0].raw ?? values);
    const colour = plotted[0].color;
    const delta = data?.delta_pct;
    const deltaTone = typeof delta !== 'number' || delta === 0
      ? 'neutral'
      : (delta > 0) === (positiveDirection !== 'DOWN') ? 'success' : 'critical';

    /*
      The last drawn point, for the end marker.

      Scanned from the right rather than taken as `values.at(-1)`: a trailing
      gap is a period that has not reported yet, and putting the dot in mid-air
      above the axis would claim a value for it.
    */
    const lastIndex = values.reduce(
      (found, value, index) => (typeof value === 'number' ? index : found),
      -1,
    );

    return (
      <div className={`flex items-center gap-3 ${className}`.trim()}>
        <span className="flex shrink-0 flex-col gap-0.5">
          <span className="text-2xl font-bold leading-none text-content">
            {formatValue(headline, plot?.format)}
          </span>
          {typeof delta === 'number' ? (
            <span className={`text-[11px] font-medium leading-none ${toneFor(deltaTone).text}`}>
              {formatRatio(delta)}
            </span>
          ) : null}
        </span>

        <span className="relative block min-w-0 flex-1">
          <svg
            width="100%"
            height="34"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="block"
            aria-hidden
          >
            {fill ? (
              <path
                d={`${polylinePath(values)} L 100,100 L 0,100 Z`}
                fill={chartAlpha(colour, 0.18)}
                stroke="none"
              />
            ) : null}
            <path
              d={polylinePath(values)}
              fill="none"
              stroke={colour}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              className="wg-enter-draw"
            />
          </svg>

          {lastIndex >= 0 ? (
            <span
              aria-hidden
              className="absolute block h-[5px] w-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                background: colour,
                left: `${(lastIndex / Math.max(1, values.length - 1)) * 100}%`,
                top: `${100 - values[lastIndex]}%`,
              }}
            />
          ) : null}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex h-full min-h-0 flex-col ${className}`.trim()}>
      {legendPosition === 'top' ? <ChartLegend items={legendItems} /> : null}
      {axis?.label ? (
        <span className="text-[10px] text-content-muted">{axis.label}</span>
      ) : null}

      {/*
        Two columns: the value axis sizes itself to its widest tick and the
        plot takes the rest. The x labels sit in the plot's column so they
        stay under the marks rather than under the gutter.
      */}
      <div
        className="grid min-h-0 flex-1 gap-x-1.5"
        style={{ gridTemplateColumns: 'auto minmax(0, 1fr)', gridTemplateRows: 'minmax(0, 1fr) auto' }}
      >
        <ChartValueAxis ticks={scale.ticks} format={axis?.format ?? plot?.format} />

        <div className="relative min-h-0" style={{ minHeight: PLOT_MIN_PX }}>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0"
          aria-hidden
        >
          <ChartGrid />
          {plotted.map((entry, index) => (
            <g key={entry.key}>
              {isArea ? (
                <path
                  d={stackedBandPath(entry.lower ?? [], entry.upper ?? [])}
                  /* Near-solid: the bands tile, so nothing is behind one to
                     show through. The old 0.18 was for an overlaid area and
                     left two adjacent bands the same washed-out tint. */
                  fill={chartAlpha(entry.color, 0.9)}
                  className="wg-enter-fade"
                />
              ) : null}
              <path
                d={polylinePath(entry.values)}
                fill="none"
                stroke={entry.color}
                strokeWidth={isArea ? '1' : '2'}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                className="wg-enter-draw"
                style={{ animationDelay: `${index * 120}ms` }}
              />
            </g>
          ))}
        </svg>

        <div className="absolute inset-0">
          {plotted.map((entry) =>
            entry.values.map((value, index) => (value === null ? null : (
              <span
                key={`${entry.key}-${categories[index] ?? index}`}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{
                  left: `${(index / Math.max(1, entry.values.length - 1)) * 100}%`,
                  top: `${100 - value}%`,
                }}
              >
                <ChartDataPoint
                  label={`${categories[index] ?? ''} · ${entry.label} ${formatValue((entry.raw ?? entry.values)[index], plot?.format)}`.trim()}
                >
                  {/*
                    A visible dot inside an invisible hit area.

                    The marker used to appear only on hover, which kept the
                    line clean and cost the reader the one thing a line chart
                    hides: how many observations it is drawn from. Six points
                    and sixty look identical as a curve. The dot is the
                    evidence; the 14px square around it is what makes it
                    easy to hit.

                    Only on `line` — an area stacks its bands and a sparkline
                    is sized to sit beside a number, and dots on either turn
                    the drawing into texture.
                  */}
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    {showMarkers ? (
                      <span
                        aria-hidden
                        className="block h-[5px] w-[5px] rounded-full"
                        style={{ background: entry.color, boxShadow: `0 0 0 2px ${CHART_INK.surface}` }}
                      />
                    ) : (
                      <span
                        aria-hidden
                        className="block h-3.5 w-3.5 rounded-full opacity-0 transition-opacity duration-100 hover:opacity-100"
                        style={{ background: entry.color }}
                      />
                    )}
                  </span>
                </ChartDataPoint>
              </span>
            ))),
          )}
        </div>
        </div>

        {/* Row 2, column 2: under the plot, never under the axis gutter. */}
        <div style={{ gridColumn: 2 }}>
          <ChartAxisLabels labels={categories} />
        </div>
      </div>

      {legendPosition === 'bottom' ? <ChartLegend items={legendItems} /> : null}
      <WidgetPeriodChip period={data?.period} className="mt-1 self-start" />
    </div>
  );
}

LineChartWidget.propTypes = {
  /**
   * `series_chart` for LINE / AREA / STACKED_BAR, a `SPARKLINE`'s own points,
   * or the pre-v2 parallel arrays. See `seriesChartToPlot`.
   */
  data: PropTypes.shape({
    // series_chart
    x_axis: PropTypes.object,
    y_axes: PropTypes.arrayOf(PropTypes.object),
    series: PropTypes.arrayOf(PropTypes.object),
    // sparkline
    points: PropTypes.arrayOf(PropTypes.object),
    summary_value: PropTypes.number,
    format: PropTypes.object,
    min: PropTypes.number,
    max: PropTypes.number,
    delta_pct: PropTypes.number,
    period: PropTypes.object,
    // pre-v2
    categories: PropTypes.arrayOf(PropTypes.string),
    unit: PropTypes.string,
  }).isRequired,
  variant: PropTypes.oneOf(['line', 'area', 'sparkline']),
  fillGaps: PropTypes.oneOf(['GAP', 'ZERO']),
  legendPosition: PropTypes.oneOf(['top', 'bottom']),
  fill: PropTypes.bool,
  positiveDirection: PropTypes.oneOf(['UP', 'DOWN']),
  accent: PropTypes.string,
  className: PropTypes.string,
};

export default memo(LineChartWidget);
